/**
 * TeamSwitcher - dev-only dropdown for testing as different teams within a league.
 *
 * Renders only when SeasonContext's isTeamSwitcherEnabled() is true.
 * Visible top-right on /league/* routes.
 *
 * Carryforward note: this component is REMOVED from production builds before launch.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useSeason, isTeamSwitcherEnabled } from "../../contexts/SeasonContext";
import { authHeaders } from "../../contexts/AuthContext";
import { Users, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamSwitcher() {
  const params = useParams();
  const leagueId = params.id || params.leagueId;
  const { actingAsTeamId, setActingAsTeamId } = useSeason();
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!leagueId || !isTeamSwitcherEnabled()) return;
    // Dev-only switcher - non-essential, degrades to empty list if fetch fails.
    axios.get(`${API}/season/${leagueId}`, { headers: authHeaders() })
      .then((r) => setTeams(r.data?.teams || []))
      .catch((_e) => setTeams([]));
  }, [leagueId]);

  if (!isTeamSwitcherEnabled() || !leagueId) return null;
  if (teams.length === 0) return null;

  const current = teams.find((t) => t.id === actingAsTeamId);

  return (
    <div className="relative" data-testid="team-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#FF6B00]/10 border border-[#FF6B00]/40 text-[#FF6B00] hover:bg-[#FF6B00]/20 transition-all text-sm font-display font-bold uppercase tracking-wide"
        data-testid="team-switcher-trigger"
        title="DEV: act as another team"
      >
        <Users size={14} />
        {current ? current.team_name || "Unnamed" : "Choose Team"}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 max-h-96 overflow-y-auto rounded-xl bg-[#181818] border border-[#FF6B00]/30 shadow-2xl z-50 backdrop-blur-md">
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[#FF6B00] font-bold border-b border-[#FF6B00]/20 flex items-center justify-between">
            <span>DEV - Act as Team</span>
            <button onClick={() => setOpen(false)} className="text-[#A0A0A0] hover:text-white">
              <X size={14} />
            </button>
          </div>
          <button
            onClick={() => { setActingAsTeamId(null); setOpen(false); }}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-[#FF6B00]/10 transition ${!actingAsTeamId ? "text-[#FF6B00] font-bold" : "text-[#E5E5E5]"}`}
            data-testid="team-switcher-clear"
          >
            <div>- Real auth user -</div>
            <div className="text-[10px] text-[#888]">Use the real logged-in account</div>
          </button>
          <div className="border-t border-[#2A2A2A]" />
          {teams.map((t) => (
            <button
              key={t.id}
              onClick={() => { setActingAsTeamId(t.id); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[#FF6B00]/10 transition ${t.id === actingAsTeamId ? "text-[#FF6B00] font-bold" : "text-[#E5E5E5]"}`}
              data-testid={`team-switcher-option-${t.id}`}
            >
              <div className="flex items-center justify-between">
                <span>{t.team_name || "Unnamed Team"}</span>
                <span className="text-[10px] text-[#888]">{t.wins}-{t.losses}-{t.ties}</span>
              </div>
              <div className="text-[10px] text-[#888]">FAAB ${t.faab_remaining ?? "-"} · PF {Math.round(t.points_for || 0)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
